'use client';

import { useEffect, useState } from 'react';

import { useAccount } from 'wagmi';

import {
  useUpdateGlobalData,
  useGlobalData
} from '@/providers/GlobalDataProvider';
import { getUserAction } from '@/server/actions/user/getUser.action';

// Helper function to get cookie value by name
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

export function useSessionValidation() {
  const { isConnected, address } = useAccount();
  const updateGlobalData = useUpdateGlobalData();
  const user = useGlobalData('user');
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const validateSession = async () => {
      // Only validate if wallet is connected but no user data
      // This handles the case where wallet is connected but session might be expired
      if (isConnected && address && !user) {
        // Check if session_token exists in cookies before making the request
        const sessionToken = getCookie('session_token');

        if (!sessionToken) {
          console.log(
            'Session validation: No session_token cookie found, skipping validation'
          );
          return;
        }

        console.log(
          'Session validation: Wallet connected but no user data, validating session'
        );
        setIsValidating(true);

        try {
          const response = await getUserAction({ setResponseCookies: false });
          const userData = response?.data;

          if (userData) {
            console.log(
              'Session validation: Valid session found, updating user data'
            );
            updateGlobalData({ user: userData });
          } else {
            console.log(
              'Session validation: No valid session found, user remains null'
            );
          }
        } catch (error) {
          console.log(
            'Session validation: Session validation failed, user remains null',
            error
          );
          // Don't update user data on error - let it remain null
        } finally {
          setIsValidating(false);
        }
      }
    };
    // Add a small delay to prevent validation on every render
    const timeoutId = setTimeout(validateSession, 100);

    return () => clearTimeout(timeoutId);
  }, [isConnected, address, user, updateGlobalData]);

  return { isValidating };
}
